import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Loader2, Plus, Trash2, Save, X, Pencil } from "lucide-react";

const EMPTY = { slug: "", label: "", color: "#facc15", icon: "", order: 0 };

export default function PoiCategoriesManager() {
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [draft, setDraft] = useState(EMPTY);
    const [editingId, setEditingId] = useState(null);
    const [saving, setSaving] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const res = await api.get("/poi-categories");
            setCategories(res.data);
        } catch (e) {
            setError(formatApiErrorDetail(e.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const startEdit = (cat) => {
        setEditingId(cat.id);
        setDraft({
            slug: cat.slug,
            label: cat.label,
            color: cat.color || "#facc15",
            icon: cat.icon || "",
            order: cat.order || 0,
        });
        setError("");
    };

    const cancelEdit = () => {
        setEditingId(null);
        setDraft(EMPTY);
        setError("");
    };

    const submit = async () => {
        if (!draft.label.trim()) {
            setError("Podaj etykietę kategorii.");
            return;
        }
        setSaving(true);
        setError("");
        try {
            const payload = {
                slug: draft.slug || draft.label,
                label: draft.label,
                color: draft.color,
                icon: draft.icon,
                order: Number(draft.order) || 0,
            };
            if (editingId) {
                await api.put(`/poi-categories/${editingId}`, payload);
            } else {
                await api.post("/poi-categories", payload);
            }
            cancelEdit();
            await load();
        } catch (e) {
            setError(formatApiErrorDetail(e.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    const remove = async (cat) => {
        if (!window.confirm(
            `Usunąć kategorię "${cat.label}"? POI z tej kategorii zostaną przepisane na "general".`
        )) return;
        try {
            await api.delete(`/poi-categories/${cat.id}`);
            await load();
        } catch (e) {
            setError(formatApiErrorDetail(e.response?.data?.detail));
        }
    };

    return (
        <div data-testid="admin-poi-categories" className="grid lg:grid-cols-12 gap-8">
            <div className="lg:col-span-5">
                <div className="panel-yellow p-6">
                    <div className="label-eyebrow mb-3">
                        {editingId ? "Edycja kategorii" : "Nowa kategoria"}
                    </div>
                    <Field label="Etykieta">
                        <input
                            data-testid="cat-label-input"
                            value={draft.label}
                            onChange={(e) =>
                                setDraft((d) => ({ ...d, label: e.target.value }))
                            }
                            placeholder="np. Warsztat"
                            className="input"
                        />
                    </Field>
                    <Field label="Slug (identyfikator, auto z etykiety jeśli puste)">
                        <input
                            data-testid="cat-slug-input"
                            value={draft.slug}
                            onChange={(e) =>
                                setDraft((d) => ({ ...d, slug: e.target.value }))
                            }
                            placeholder="np. warsztat"
                            className="input font-mono"
                        />
                    </Field>
                    <Field label="Kolor markera">
                        <div className="flex items-center gap-3">
                            <input
                                type="color"
                                data-testid="cat-color-input"
                                value={draft.color}
                                onChange={(e) =>
                                    setDraft((d) => ({ ...d, color: e.target.value }))
                                }
                                className="h-10 w-14 rounded border border-white/10 bg-transparent cursor-pointer"
                            />
                            <input
                                value={draft.color}
                                onChange={(e) =>
                                    setDraft((d) => ({ ...d, color: e.target.value }))
                                }
                                className="input font-mono flex-1"
                                placeholder="#facc15"
                            />
                        </div>
                    </Field>
                    <Field label="Emoji / ikona (opcjonalnie)">
                        <input
                            data-testid="cat-icon-input"
                            value={draft.icon}
                            onChange={(e) =>
                                setDraft((d) => ({ ...d, icon: e.target.value }))
                            }
                            placeholder="np. 🏢 ⛽ ⭐ 🍕"
                            maxLength={4}
                            className="input text-lg"
                        />
                    </Field>
                    <Field label="Kolejność">
                        <input
                            type="number"
                            data-testid="cat-order-input"
                            value={draft.order}
                            onChange={(e) =>
                                setDraft((d) => ({ ...d, order: e.target.value }))
                            }
                            className="input"
                        />
                    </Field>

                    {/* Preview */}
                    <div className="mt-2 mb-3 text-[10px] uppercase tracking-[0.25em] text-[color:var(--text-secondary)]">
                        Podgląd
                    </div>
                    <div className="flex items-center gap-3 p-3 rounded-md border border-white/10 bg-[color:var(--bg-base)]">
                        <div
                            className="rounded-full border-2 border-white flex items-center justify-center text-sm"
                            style={{
                                background: draft.color,
                                width: 24,
                                height: 24,
                                color: "#000",
                            }}
                            title="Marker preview"
                        >
                            {draft.icon}
                        </div>
                        <div className="text-sm text-white">
                            {draft.label || "(etykieta)"}
                        </div>
                    </div>

                    {error && (
                        <div
                            className="text-sm text-red-400 mt-3"
                            data-testid="cat-error"
                        >
                            {error}
                        </div>
                    )}
                    <div className="flex items-center gap-2 mt-4">
                        <button
                            data-testid="cat-save-btn"
                            onClick={submit}
                            disabled={saving}
                            className="btn-primary"
                        >
                            {saving ? (
                                <Loader2 className="animate-spin" size={14} />
                            ) : editingId ? (
                                <Save size={14} />
                            ) : (
                                <Plus size={14} />
                            )}
                            {editingId ? "Zapisz" : "Dodaj kategorię"}
                        </button>
                        {editingId && (
                            <button
                                data-testid="cat-cancel-btn"
                                onClick={cancelEdit}
                                className="text-xs uppercase tracking-wider px-3 py-2 rounded-md border border-white/10 text-[color:var(--text-secondary)] hover:text-white inline-flex items-center gap-1.5"
                            >
                                <X size={14} /> Anuluj
                            </button>
                        )}
                    </div>
                </div>
            </div>

            <div className="lg:col-span-7">
                <div className="panel-yellow p-6">
                    <div className="label-eyebrow mb-3">
                        Kategorie ({categories.length})
                    </div>
                    {loading ? (
                        <Loader2 className="animate-spin text-[color:var(--text-secondary)]" />
                    ) : categories.length === 0 ? (
                        <div className="text-sm text-[color:var(--text-secondary)]">
                            Brak kategorii.
                        </div>
                    ) : (
                        <ul className="space-y-2">
                            {categories.map((c) => (
                                <li
                                    key={c.id}
                                    data-testid={`cat-row-${c.slug}`}
                                    className={`flex items-center gap-3 p-3 border rounded-md ${
                                        editingId === c.id
                                            ? "border-[color:var(--primary)] bg-[color:var(--primary)]/5"
                                            : "border-white/5"
                                    }`}
                                >
                                    <div
                                        className="rounded-full border-2 border-white flex items-center justify-center text-sm shrink-0"
                                        style={{
                                            background: c.color,
                                            width: 28,
                                            height: 28,
                                            color: "#000",
                                        }}
                                    >
                                        {c.icon}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm text-white font-medium truncate">
                                            {c.label}
                                        </div>
                                        <div className="text-[10px] uppercase tracking-wider text-[color:var(--text-muted)] mt-0.5 font-mono">
                                            {c.slug} · {c.color} · #{c.order}
                                        </div>
                                    </div>
                                    <button
                                        data-testid={`cat-edit-${c.slug}`}
                                        onClick={() => startEdit(c)}
                                        className="text-[color:var(--text-secondary)] hover:text-white p-1.5"
                                        title="Edytuj"
                                    >
                                        <Pencil size={14} />
                                    </button>
                                    <button
                                        data-testid={`cat-delete-${c.slug}`}
                                        onClick={() => remove(c)}
                                        className="text-red-400 hover:text-red-300 p-1.5"
                                        title="Usuń"
                                    >
                                        <Trash2 size={14} />
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>

            <style>{`
                .input { width:100%; background: var(--bg-base); border:1px solid rgba(255,255,255,0.1); border-radius:6px; padding:8px 12px; color:white; font-size:14px; }
                .input:focus { outline:none; border-color: var(--primary); }
            `}</style>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <label className="block mb-3">
            <span className="block text-[10px] uppercase tracking-[0.25em] text-[color:var(--text-secondary)] mb-1.5">
                {label}
            </span>
            {children}
        </label>
    );
}
